"use client";

/**
 * Admin "simulate as tier" — operator pre-deploy verification lever.
 *
 * Backend contract: /api/v2/admin/tier-simulation (GET/PUT/DELETE),
 * superuser-only. While a simulation is active the backend resolves the
 * admin's tier limits as the SIMULATED tier, so real enforcement (group /
 * account / chat caps) fires exactly as it would for a member on that tier.
 *
 * Two surfaces, one module:
 * - `TierSimulationBanner` — persistent fixed banner while simulating
 *   (OfflineBanner pattern; deliberately NOT dismissible — the admin must
 *   never forget they are throttled), with an Exit control.
 * - `TierSimulationCard` — the admin-settings control to pick and activate
 *   a tier.
 *
 * Polling is admin-surface only (`/admin/*`) AND `is_admin`, and uses the
 * admin-only BFF proxy. Never refetch after an error.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { FlaskConical, LoaderIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { adminApiClient } from "@/lib/admin-api-client";
export type TierSimulationState = {
  simulated_tier: string | null;
  available_tiers: string[];
};
export const TIER_SIMULATION_QUERY_KEY = ["admin", "tier-simulation"] as const;
const TIER_LABELS: Record<string, string> = {
  free: "Free (no subscription)",
  m1_operator: "M1 Operator",
  founding_member: "Founding Member",
  standard_monthly: "Standard Monthly",
  m2_desk: "M2 Desk",
  m3_fund: "M3 Fund",
  accelerator: "Desk: Accelerator (retired)",
  scale: "Desk: Scale (retired)",
  unlimited: "Desk: Unlimited (retired)",
  comped: "Comped",
  companion: "Companion"
};
export function tierLabel(value: string): string {
  return TIER_LABELS[value] ?? value;
}
function useIsAdmin(): boolean {
  const {
    data: session
  } = useSession();
  const user = session?.user as {
    is_admin?: boolean;
  } | undefined;
  return user?.is_admin === true;
}
export function useTierSimulation() {
  const isAdmin = useIsAdmin();
  const pathname = usePathname() ?? "";
  const onAdminSurface = pathname.startsWith("/admin");
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: TIER_SIMULATION_QUERY_KEY,
    enabled: isAdmin && onAdminSurface,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: q => q.state.status === "success" ? 30_000 : false,
    queryFn: async (): Promise<TierSimulationState> => {
      const resp = await adminApiClient.getV2<TierSimulationState>("/admin/tier-simulation", {
        expectedErrorStatuses: [403]
      });
      if (resp.error) throw new Error("tier-simulation fetch failed");
      return resp.data;
    }
  });
  const activate = useMutation({
    mutationFn: async (tier: string): Promise<TierSimulationState> => {
      const resp = await adminApiClient.putV2<TierSimulationState>("/admin/tier-simulation", {
        tier
      });
      if (resp.error) throw new Error("tier-simulation activate failed");
      return resp.data;
    },
    onSuccess: () => {
      // Limits changed server-side — refresh simulation state AND usage meters.
      void queryClient.invalidateQueries({
        queryKey: TIER_SIMULATION_QUERY_KEY
      });
      void queryClient.invalidateQueries({
        queryKey: ["usage"]
      });
    }
  });
  const clear = useMutation({
    mutationFn: async (): Promise<TierSimulationState> => {
      const resp = await adminApiClient.deleteV2<TierSimulationState>("/admin/tier-simulation");
      if (resp.error) throw new Error("tier-simulation clear failed");
      return resp.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TIER_SIMULATION_QUERY_KEY
      });
      void queryClient.invalidateQueries({
        queryKey: ["usage"]
      });
    }
  });
  return {
    isAdmin,
    query,
    activate,
    clear
  };
}

/**
 * Persistent banner while a tier simulation is active. Fixed overlay (same
 * placement contract as OfflineBanner), purple so it never reads as an error,
 * not dismissible — only "Exit simulation" removes it.
 */
export function TierSimulationBanner() {
  const {
    isAdmin,
    query,
    clear
  } = useTierSimulation();
  const tier = query.data?.simulated_tier ?? null;
  if (!isAdmin || !tier) return null;
  return <div className="fixed inset-x-0 top-0 z-[9998] flex items-center justify-center gap-2 bg-purple-600/95 px-4 py-2 text-sm font-medium text-purple-50 shadow-md backdrop-blur-sm" role="status" aria-live="polite" data-testid="tier-simulation-banner" data-sentry-component="TierSimulationBanner" data-sentry-source-file="tier-simulation.tsx">
      <FlaskConical className="h-4 w-4 shrink-0" data-sentry-element="FlaskConical" data-sentry-source-file="tier-simulation.tsx" />
      <span>
        Simulating tier: <strong>{tierLabel(tier)}</strong> — tier limits are enforced on your
        account as a member would see them.
      </span>
      <button type="button" onClick={() => clear.mutate()} disabled={clear.isPending} className="ml-2 inline-flex items-center gap-1 rounded-sm border border-purple-300/40 px-2 py-0.5 text-xs hover:bg-purple-500/40 transition-colors disabled:opacity-60">
        {clear.isPending ? <LoaderIcon className="h-3 w-3 animate-spin" /> : <XIcon className="h-3 w-3" />}
        Exit simulation
      </button>
    </div>;
}

/** Admin-settings control: pick a tier and activate/exit the simulation. */
export function TierSimulationCard() {
  const {
    query,
    activate,
    clear
  } = useTierSimulation();
  const [selected, setSelected] = useState<string>("");
  const state = query.data;
  const active = state?.simulated_tier ?? null;
  const tiers = state?.available_tiers ?? [];
  return <Card noBorder className="glass glass-lit bg-card/50 backdrop-blur-sm border-border/50" data-testid="tier-simulation-card" data-sentry-element="Card" data-sentry-component="TierSimulationCard" data-sentry-source-file="tier-simulation.tsx">
      <CardHeader data-sentry-element="CardHeader" data-sentry-source-file="tier-simulation.tsx">
        <CardTitle className="flex items-center gap-2" data-sentry-element="CardTitle" data-sentry-source-file="tier-simulation.tsx">
          <FlaskConical className="h-5 w-5 text-purple-400" data-sentry-element="FlaskConical" data-sentry-source-file="tier-simulation.tsx" />
          Tier Simulation
        </CardTitle>
        <CardDescription data-sentry-element="CardDescription" data-sentry-source-file="tier-simulation.tsx">
          Drive the app under a member tier&apos;s limits and watch enforcement fire (e.g. simulate
          M1 Operator, then try to create a 2nd group). Applies only to your admin account; every
          activation is audit-logged.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4" data-sentry-element="CardContent" data-sentry-source-file="tier-simulation.tsx">
        {active ? <div className="flex items-center justify-between gap-4 rounded-md border border-purple-500/40 bg-purple-500/10 px-3 py-2">
            <span className="text-sm">
              Active: <strong>{tierLabel(active)}</strong>
            </span>
            <Button variant="outline" size="sm" onClick={() => clear.mutate()} disabled={clear.isPending}>
              {clear.isPending ? <LoaderIcon className="h-4 w-4 animate-spin" /> : "Exit simulation"}
            </Button>
          </div> : <div className="space-y-2">
            <Label htmlFor="tier-simulation-select">Simulate as</Label>
            <div className="flex items-center gap-2">
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger id="tier-simulation-select" className="bg-section border border-input-border">
                  <SelectValue placeholder="Pick a tier" />
                </SelectTrigger>
                <SelectContent>
                  {tiers.map(tier => <SelectItem key={tier} value={tier}>
                      {tierLabel(tier)}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => selected && activate.mutate(selected)} disabled={!selected || activate.isPending}>
                {activate.isPending ? <LoaderIcon className="h-4 w-4 animate-spin" /> : "Activate"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              While active, a persistent banner shows on every page until you exit.
            </p>
          </div>}
      </CardContent>
    </Card>;
}